import { Button } from 'antd';
import { momentForDomain } from '@common/services/moment-shadow';
import React from 'react';
import { ShiftGapDetailsQueryCgResponse } from '@common/graphql/schema';
import { Gap, Gutter, Icon, Tag, Text, Col, Row } from '@common/components';
import { getShiftSourceDisplay } from '@scheduling/services';
import { formatNumberDigits } from '@common/services';
import { FontSize } from '@common/tokens';
import { useFeatureFlags } from '@common/contexts/feature-flags';

const moment = momentForDomain('scheduling', 'shifts');

export function ShiftRequestDetailsContent({
  shift,
  setShiftEditMode = () => {},
  showOpeningCount,
  matchBreakdownEditMode = false,
  canEditShiftDetails = false,
  tagsEnabled = false,
}: {
  shift: ShiftGapDetailsQueryCgResponse['shiftGap'];
  showOpeningCount?: boolean;
  setShiftEditMode?: (value: boolean) => void;
  matchBreakdownEditMode?: boolean;
  canEditShiftDetails?: boolean;
  tagsEnabled?: boolean;
}) {
  const { isShiftGapFillRateDisplayEnabled } = useFeatureFlags();

  const isNightShift = shift.timeOfDay === 'night';

  const shiftStart = moment.utc(shift.startDatetime).tz(shift.timeZone);
  const shiftEnd = moment.utc(shift.endDatetime).tz(shift.timeZone);

  const shiftRangeText = `${shiftStart.format('HH:mm')} - ${shiftEnd.format(
    'HH:mm'
  )}`;

  const titleSpan = showOpeningCount ? 4 : 3;
  const shiftSourceDisplay = getShiftSourceDisplay(shift?.source);

  const sourceInfo = (
    <Gap align="center" size="xs">
      <Icon icon={shiftSourceDisplay.icon} kind="light" />
      <Text>{shiftSourceDisplay.label}</Text>
    </Gap>
  );

  return (
    <>
      <Row justify="space-between">
        <Row gutter={[8, 20]} align="middle">
          <Col>
            <Text size="lg" weight="medium">
              {shiftStart.format('dddd, MMMM D')}{' '}
            </Text>
          </Col>
          <Col>
            <Icon
              icon={isNightShift ? 'moon' : 'sun'}
              kind="solid"
              size="md"
              color={isNightShift ? 'skyRegular' : 'harvestGold'}
            />
          </Col>
          <Col>
            <Text size="lg" weight="medium">
              {shiftRangeText} ({formatNumberDigits(shift.shiftDuration)} hrs)
            </Text>
          </Col>
          {(canEditShiftDetails || tagsEnabled) && !matchBreakdownEditMode && (
            <Button type="link" onClick={() => setShiftEditMode(true)}>
              <Icon icon="pen" kind="solid" size="md" />
            </Button>
          )}
        </Row>
        {sourceInfo}
      </Row>
      <div style={{ fontSize: FontSize.sm, marginTop: '16px' }}>
        <Row gutter={[0, 8]}>
          <Col span={titleSpan}>
            <Text secondary>Role</Text>
          </Col>
          <Col span={16}>{shift?.role?.abbreviation}</Col>
        </Row>

        <Row gutter={[0, 8]} style={{ marginTop: 12 }}>
          <Col span={titleSpan}>
            <Text secondary>Unit</Text>
          </Col>
          <Col span={16}>{shift.organizationUnit.name}</Col>
        </Row>

        <Row gutter={[0, 8]} style={{ marginTop: 12 }}>
          <Col span={titleSpan}>
            <Text secondary>CC</Text>
          </Col>
          <Col span={16}>{shift.organizationUnit.costCenter}</Col>
        </Row>

        {showOpeningCount && (
          <Row gutter={[0, 8]} style={{ marginTop: 12 }}>
            <Col span={titleSpan}>
              <Text secondary>Openings</Text>
            </Col>
            <Col span={24 - titleSpan}>{shift.openShiftGapOpeningsCount}</Col>
          </Row>
        )}

        {isShiftGapFillRateDisplayEnabled &&
          shift.fillRate !== null &&
          shift.fillRate !== undefined && (
            <Gutter direction="top" size="md">
              <Row gutter={[0, 8]}>
                <Col span={titleSpan}>
                  <Text secondary>Fill Rate</Text>
                </Col>
                <Col span={24 - titleSpan}>
                  {Math.round(shift.fillRate * 100)}%
                </Col>
              </Row>
            </Gutter>
          )}

        {tagsEnabled && (
          <Row gutter={[0, 8]} style={{ marginTop: 12 }}>
            <Col span={titleSpan}>
              <Text secondary>Tags</Text>
            </Col>
            <Col span={16}>
              {shift.shiftGapTags ? (
                <Gap wrap>
                  {shift.shiftGapTags.map((tag) => (
                    <Tag color="skyLightest" key={tag.id}>
                      <Text weight="medium">{tag?.name}</Text>
                    </Tag>
                  ))}
                </Gap>
              ) : (
                <Text>None</Text>
              )}
            </Col>
          </Row>
        )}
      </div>
    </>
  );
}
